import type { LabelInValue } from 'models';
import { useTranslation } from 'react-i18next';
import { isEmail } from 'react-multi-email';
import { Form, FormActions, Input, Modal, MultiValueInput } from 'ui';

export type EmailInviteFormValues = {
  communityId: string;
  emails: string[];
};

type P = {
  community: LabelInValue;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (data: EmailInviteFormValues) => void;
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item): item is string => typeof item === 'string');

const EmailInviteForm = ({ community, isSubmitting, onCancel, onSubmit }: P) => {
  const { t } = useTranslation();

  return (
    <Form<EmailInviteFormValues> initialValues={{ communityId: community.value }} onFinish={onSubmit}>
      <Form.Item<EmailInviteFormValues> hidden name="communityId" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item<EmailInviteFormValues>
        extra="Type email address and press enter, space or comma - Or paste from a spreadsheet!"
        label="Emails"
        name="emails"
        rules={[
          {
            // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
            async validator(_, value: unknown) {
              if (!isStringArray(value)) {
                return;
              }

              const invalidEmails = value.filter((email) => !isEmail(email));
              if (invalidEmails.length > 0) {
                throw new Error(
                  t('Error: Some emails are invalid. Please enter a valid email address (e.g., example@email.com)'),
                );
              }
            },
          },
          {
            required: true,
            type: 'array',
          },
        ]}
      >
        <MultiValueInput allowInvalidValues />
      </Form.Item>
      <Modal.HugContent.Footer>
        <FormActions
          right={{
            children: 'Back',
            onClick() {
              onCancel();
            },
          }}
          submit={{
            children: 'Send invitations',
            loading: isSubmitting,
          }}
        />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default EmailInviteForm;
